import { useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, Download, ExternalLink, FileText, Swords, Leaf, Loader2, X, CheckCircle2, Layers, Presentation, Sprout } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { getVisitorId, trackEvent } from "../../lib/track";
import { ModelingDisclaimer } from "./ModelingDisclaimer";

// Per-product public resources. Only products with collateral appear.
const RES = {
  "big-bad-mats": {
    calc: "/big-bad-mats-calculator",
    calcTitle: "Calculate Your Lifecycle ROI",
    calcSub: "Model total cost of ownership vs budget mats — and email yourself the full analysis.",
    docs: [
      { icon: FileText, title: "Case Study", sub: "60-day pipeline ROW: 5,000 mats, zero replacements.", view: "/big-bad-mats-case-study", pdf: "/big-bad-mats-case-study.pdf", gated: true },
      { icon: FileText, title: "Technical Data Sheet", sub: "Dimensions, load ratings, material & install specs.", view: "/big-bad-mats-datasheet", pdf: "/big-bad-mats-technical-data-sheet.pdf" },
      { icon: Swords, title: "Competitive Battlecard", sub: "Big Bad Mats vs composite, timber & steel.", view: "/big-bad-mats-battlecard", pdf: "/big-bad-mats-battlecard.pdf" },
      { icon: Leaf, title: "Sustainability / ESG Brief", sub: "Recyclable, non-leach, lower lifecycle footprint.", view: "/big-bad-mats-sustainability", pdf: "/big-bad-mats-sustainability.pdf" },
      { icon: Layers, title: "Buy vs. Service Flyer", sub: "One page: buy the product or full turnkey service — any mat.", view: "/ground-protection-flyer", pdf: "/ground-protection-flyer.pdf" },
      { icon: Presentation, title: "Two Avenues Deck", sub: "7 slides: buy the product vs. turnkey service — any mat.", view: "/ground-protection-deck", pdf: "/ground-protection-deck.pdf" },
      { icon: Presentation, title: "Resource Preservation Flyer", sub: "One-page overview of Big Bad Mats & our single-source mat program.", view: "/mats-presentation-flyer.pdf", pdf: "/mats-presentation-flyer.pdf", thumb: "/mats-presentation-flyer-thumb.png", external: true },
    ],
  },
  bonedry: {
    calc: "/collateral/bonedry/calculator",
    calcTitle: "Calculate Your Cost Per Spill",
    calcSub: "Model true cost per incident vs conventional clay — and email yourself the analysis.",
    docs: [
      { icon: FileText, title: "Technical Data Sheet", sub: "Physical, performance, application & safety data.", view: "/collateral/bonedry/datasheet", pdf: "/bonedry-data-sheet.pdf" },
      { icon: Swords, title: "Competitive Battlecard", sub: "BoneDry vs clay, pads & vac/sweep.", view: "/collateral/bonedry/battlecard", pdf: "/bonedry-battlecard.pdf" },
      { icon: Leaf, title: "Environmental & Compliance", sub: "Encapsulation, silica-free, SPCC support.", view: "/collateral/bonedry/compliance", pdf: "/bonedry-compliance-brief.pdf" },
      { icon: Presentation, title: "Resource Preservation Flyer", sub: "One-page BoneDry product overview — features, uses & value.", view: "/bonedry-presentation-flyer.pdf", pdf: "/bonedry-presentation-flyer.pdf", thumb: "/bonedry-presentation-flyer-thumb.png", external: true },
    ],
  },
  pinkwater: {
    calc: "/collateral/pinkwater/calculator",
    calcTitle: "Calculate Your Water Savings",
    calcSub: "Model on-site treatment vs haul-off per gallon — and email yourself the analysis.",
    docs: [
      { icon: FileText, title: "Technical Data Sheet", sub: "Physical, performance, application & handling data.", view: "/collateral/pinkwater/datasheet", pdf: "/pinkwater-data-sheet.pdf" },
      { icon: Swords, title: "Competitive Battlecard", sub: "Pinkwater vs haul-off, settling & filtration.", view: "/collateral/pinkwater/battlecard", pdf: "/pinkwater-battlecard.pdf" },
      { icon: Leaf, title: "Environmental & Compliance", sub: "Discharge compliance, biodegradable, SWPPP.", view: "/collateral/pinkwater/compliance", pdf: "/pinkwater-compliance-brief.pdf" },
      { icon: Presentation, title: "Resource Preservation Flyer", sub: "One-page Pinkwater product overview — features, uses & value.", view: "/pinkwater-presentation-flyer.pdf", pdf: "/pinkwater-presentation-flyer.pdf", thumb: "/pinkwater-presentation-flyer-thumb.png", external: true },
    ],
  },
};

function GateModal({ doc, productName, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required."); return; }
    setSending(true);
    try {
      await api.post("/quote", {
        name: form.name, email: form.email, company: form.company || "—",
        industry: `${productName} — ${doc.title} download`,
        message: `Resource download request: ${doc.title} (${productName}) from product page.`,
        visitor_id: getVisitorId(),
      });
      trackEvent("asset_download", { email: form.email, label: `${productName} — ${doc.title} (PDF)`, product: productName, path: doc.pdf });
      toast.success("Thanks — opening your download.");
      window.open(doc.pdf, "_blank", "noopener");
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/80 backdrop-blur-sm p-4" data-testid="gate-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-md p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="gate-close" className="absolute top-4 right-4 text-muted-foreground hover:text-ink"><X className="h-5 w-5" /></button>
        <div className="kicker text-flame mb-2">Free Download</div>
        <h3 className="font-display font-extrabold text-2xl text-ink">{doc.title}</h3>
        <p className="mt-2 text-sm text-muted-foreground">{doc.sub} Tell us where to send it and we'll open it right now.</p>
        <form onSubmit={submit} className="mt-5 space-y-3" data-testid="gate-form">
          <input placeholder="Full name" value={form.name} data-testid="gate-name" onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-border px-3 py-2.5 focus:border-flame focus:outline-none" />
          <input type="email" placeholder="Work email" value={form.email} data-testid="gate-email" onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-border px-3 py-2.5 focus:border-flame focus:outline-none" />
          <input placeholder="Company (optional)" value={form.company} data-testid="gate-company" onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full border border-border px-3 py-2.5 focus:border-flame focus:outline-none" />
          <button type="submit" disabled={sending} data-testid="gate-submit" className="w-full inline-flex items-center justify-center gap-2 bg-flame text-white px-6 py-3 font-semibold hover:bg-flame/90 transition-colors disabled:opacity-60">
            {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Get the {doc.title} <Download className="h-4 w-4" /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function ProductResources({ slug, productName }) {
  const [gateDoc, setGateDoc] = useState(null);
  const r = RES[slug];
  if (!r) return null;

  return (
    <section className="bg-muted" data-testid="product-resources">
      <div className="container-x py-20">
        {/* hero calculator CTA */}
        <div className="bg-ink text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 kicker text-flame mb-3"><Calculator className="h-4 w-4" /> Interactive Tool</div>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight">{r.calcTitle}</h2>
            <p className="mt-3 text-white/60 leading-relaxed">{r.calcSub}</p>
          </div>
          <Link to={r.calc} data-testid="resources-calc-cta" className="inline-flex items-center gap-2 bg-flame text-white px-8 py-4 font-semibold hover:bg-flame/90 transition-colors flex-shrink-0">
            Launch the Calculator <ArrowRight className="h-5 w-5" />
          </Link>
        </div>

        {/* resources strip */}
        <div className="mt-6">
          <h3 className="kicker text-charcoal mb-5">Resources & Tools</h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {r.docs.map((d) => (
              <div key={d.title} data-testid={`resource-${d.title.toLowerCase().replace(/\W+/g, "-")}`} className="bg-white border border-border overflow-hidden flex flex-col">
                {d.thumb ? (
                  <a href={d.view} target="_blank" rel="noopener noreferrer" className="group relative block aspect-[297/210] bg-muted overflow-hidden border-b border-border">
                    <img src={d.thumb} alt={`${d.title} preview`} className="h-full w-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500" loading="lazy" />
                    <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/25 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 bg-white text-ink px-3 py-1.5 text-xs font-semibold"><ExternalLink className="h-3.5 w-3.5" /> Preview</span>
                    </span>
                  </a>
                ) : null}
                <div className="p-5 flex flex-col flex-1">
                {!d.thumb && <div className="h-10 w-10 flex items-center justify-center bg-flame/10 border border-flame/30"><d.icon className="h-5 w-5 text-flame" /></div>}
                <h4 className={`${d.thumb ? "" : "mt-4"} font-display font-bold text-ink leading-tight`}>{d.title}</h4>
                <p className="mt-1 text-sm text-muted-foreground leading-snug flex-1">{d.sub}</p>
                <div className="mt-4 flex items-center gap-2">
                  {d.external ? (
                    <a href={d.view} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 border border-border px-3 py-2 text-xs hover:border-flame hover:text-flame transition-colors">
                      <ExternalLink className="h-3.5 w-3.5" /> View
                    </a>
                  ) : (
                    <Link to={d.view} className="inline-flex items-center gap-1.5 border border-border px-3 py-2 text-xs hover:border-flame hover:text-flame transition-colors">
                      <ExternalLink className="h-3.5 w-3.5" /> View
                    </Link>
                  )}
                  {d.gated ? (
                    <button onClick={() => setGateDoc(d)} data-testid={`resource-gated-${d.title.toLowerCase().replace(/\W+/g, "-")}`} className="inline-flex items-center gap-1.5 bg-flame text-white px-3 py-2 text-xs font-semibold hover:bg-flame/90 transition-colors">
                      <Download className="h-3.5 w-3.5" /> Download
                    </button>
                  ) : (
                    <a href={d.pdf} download onClick={() => trackEvent("asset_download", { label: `${productName} — ${d.title} (PDF)`, product: productName, path: d.pdf })} className="inline-flex items-center gap-1.5 bg-ink text-white px-3 py-2 text-xs font-semibold hover:bg-flame transition-colors">
                      <Download className="h-3.5 w-3.5" /> PDF
                    </a>
                  )}
                </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-flame" /> Data sheets & briefs download instantly. The case study is sent to your inbox.</p>
          <div className="mt-5"><ModelingDisclaimer testid="resources-disclaimer" /></div>
        </div>
      </div>
      {gateDoc && <GateModal doc={gateDoc} productName={productName} onClose={() => setGateDoc(null)} />}
    </section>
  );
}
